import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { Bot, Search, ChevronLeft, ChevronRight, RotateCcw, LogIn } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface DigitalWorker {
  id: string
  name: string
  organizationName: string
  instanceName: string
  stationzeroEmail: string
  stationzeroEmailAccountStatus: 'provisioned' | 'not_provisioned'
  fleetProfile: string
  provisioningStatus: string
  whatsappProvider: string
  whatsappNumber: string | null
  whatsappRegistrationStatus: string
  apiEndpointUrl: string | null
  createdAt: string
  lastActivityAt: string | null
  ownerUserId: number
}

interface Pagination {
  page: number
  totalPages: number
  total: number
}

interface Props {
  workers: DigitalWorker[]
  pagination: Pagination
  search: string
  provisioningStatusFilter: string
  provisioningStatuses: string[]
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const PROVISIONING_LABELS: Record<string, string> = {
  pending: 'Pendente',
  provisioning: 'Provisionando',
  ready: 'Online',
  failed: 'Falha',
}

const PROVISIONING_COLORS: Record<string, string> = {
  pending: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  provisioning: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  ready: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
}

const PROFILE_LABELS: Record<string, string> = {
  generalist: 'Generalist',
  'ecommerce-marketing': 'E-commerce',
  'customer-success': 'CS',
  'codex-base': 'Base',
  'fde-project-lead': 'Líder FDE',
  'codex-data-scientist': 'Data Scientist',
  'codex-project-manager': 'Project Manager',
  'codex-applied-researcher': 'Applied AI Researcher',
  'codex-business-investigator': 'Business Investigator',
}

const PROFILE_COLORS: Record<string, string> = {
  generalist: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  'ecommerce-marketing': 'bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  'customer-success': 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
  'codex-base': 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-300',
  'fde-project-lead': 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  'codex-data-scientist': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
  'codex-project-manager': 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  'codex-applied-researcher': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  'codex-business-investigator': 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
}

const WA_PROVIDER_LABELS: Record<string, string> = {
  business: 'Business',
  personal: 'Personal',
  none: 'Sem WhatsApp',
}

const WA_REGISTRATION_LABELS: Record<string, string> = {
  pending: 'Pendente',
  number_purchased: 'Número comprado',
  registered_with_meta: 'Registrado Meta',
  verification_requested: 'Verificação solicitada',
  verified: 'Verificado',
  failed: 'Falha',
  not_applicable: 'N/A',
}

const WA_REGISTRATION_COLORS: Record<string, string> = {
  pending: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  number_purchased: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  registered_with_meta: 'bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  verification_requested: 'bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  verified: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  not_applicable: 'bg-accent text-muted-foreground',
}

const EMAIL_NOT_PROVISIONED_LABEL = 'Email não provisionado'

function workerEmailLabel(worker: DigitalWorker) {
  return worker.stationzeroEmailAccountStatus === 'provisioned'
    ? worker.stationzeroEmail
    : EMAIL_NOT_PROVISIONED_LABEL
}

function DigitalWorkersIndex({ workers, pagination, search, provisioningStatusFilter, provisioningStatuses }: Props) {
  const [query, setQuery] = useState(search)
  const [statusFilter, setStatusFilter] = useState(provisioningStatusFilter)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/cc/digital_workers', { search: query, provisioning_status: statusFilter }, { preserveState: true })
  }

  const handleFilterChange = (value: string) => {
    setStatusFilter(value)
    router.get('/cc/digital_workers', { search, provisioning_status: value }, { preserveState: true })
  }

  const goToPage = (page: number) => {
    router.get('/cc/digital_workers', { search, provisioning_status: provisioningStatusFilter, page }, { preserveState: true })
  }

  const retryProvisioning = (worker: DigitalWorker) => {
    router.post(`/cc/digital_workers/${worker.id}/retry_provisioning`, {
      search,
      page: pagination.page,
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bot className="h-6 w-6 text-foreground" />
          <h2 className="text-2xl font-bold text-foreground">Agentes</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
        </div>

        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="py-2 px-3 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Todos os status</option>
            {provisioningStatuses.map((status) => (
              <option key={status} value={status}>
                {PROVISIONING_LABELS[status] || status}
              </option>
            ))}
          </select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome, instância, email ou org..."
              className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-80"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Buscar
          </Button>
        </form>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-220px)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-muted border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Instância</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Perfil</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Provisioning</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">WhatsApp</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ultima mensagem</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {workers.map((worker) => (
                <tr key={worker.id} className="hover:bg-accent transition-colors">
                  <td className="px-4 py-3">
                    <Link href={`/cc/digital_workers/${worker.id}`} className="block hover:underline">
                      <div className="text-sm text-foreground">{worker.name}</div>
                      <div className="text-xs text-muted-foreground">{worker.organizationName}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground font-mono text-xs">{worker.instanceName}</div>
                    <div className="text-xs text-muted-foreground">{workerEmailLabel(worker)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PROFILE_COLORS[worker.fleetProfile] || 'bg-accent text-foreground'}`}>
                      {PROFILE_LABELS[worker.fleetProfile] || worker.fleetProfile}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PROVISIONING_COLORS[worker.provisioningStatus] || 'bg-accent text-foreground'}`}>
                      {PROVISIONING_LABELS[worker.provisioningStatus] || worker.provisioningStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">{WA_PROVIDER_LABELS[worker.whatsappProvider] || worker.whatsappProvider}</span>
                        {worker.whatsappProvider === 'business' && worker.whatsappRegistrationStatus !== 'not_applicable' && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${WA_REGISTRATION_COLORS[worker.whatsappRegistrationStatus] || 'bg-accent text-muted-foreground'}`}>
                            {WA_REGISTRATION_LABELS[worker.whatsappRegistrationStatus] || worker.whatsappRegistrationStatus}
                          </span>
                        )}
                      </div>
                      {worker.whatsappNumber && (
                        <a
                          href={`https://wa.me/${worker.whatsappNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          +{worker.whatsappNumber}
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                    {worker.lastActivityAt ? formatDate(worker.lastActivityAt) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {(worker.provisioningStatus === 'failed' ||
                        (worker.provisioningStatus === 'ready' && worker.whatsappRegistrationStatus === 'failed')) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => retryProvisioning(worker)}
                          title={worker.provisioningStatus === 'ready' ? 'Retentar configuração do WhatsApp' : 'Retentar provisionamento'}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {/* Full page navigation intentional: impersonate terminates the admin session */}
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" asChild>
                        <a href={`/users/${worker.ownerUserId}/impersonate`}>
                          <LogIn className="h-3 w-3 mr-1" />
                          Impersonar
                        </a>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {workers.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Nenhum agente encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
            <span className="text-sm text-muted-foreground">
              Pagina {pagination.page} de {pagination.totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => goToPage(pagination.page + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DigitalWorkersIndex
